import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { I18nProvider } from "@/contexts/I18nContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Tables from "@/pages/Tables";
import NewOrder from "@/pages/NewOrder";
import Kitchen from "@/pages/Kitchen";
import Cashier from "@/pages/Cashier";
import MenuPage from "@/pages/Menu";
import Expenses from "@/pages/Expenses";
import Debtors from "@/pages/Debtors";
import ReportDaily from "@/pages/ReportDaily";
import ReportBest from "@/pages/ReportBest";
import ReportMonthly from "@/pages/ReportMonthly";
import Users from "@/pages/Users";
import Settings from "@/pages/Settings";
import Receipt from "@/pages/Receipt";
import Reservations from "@/pages/Reservations";
import Inventory from "@/pages/Inventory";

const HOME_PATH_BY_ROLE = {
  owner: "/dashboard",
  manager: "/dashboard",
  accountant: "/dashboard",
  cashier: "/cashier",
  waiter: "/new-order",
  kitchen: "/kitchen",
};

function Protected({ children, allow }) {
  const { user, loading } = useAuth();
  if (loading || user === undefined)
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        در حال بارگذاری...
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (allow && !allow.includes(user.role))
    return <Navigate to={HOME_PATH_BY_ROLE[user.role] || "/dashboard"} replace />;
  return children;
}

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading || user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={HOME_PATH_BY_ROLE[user.role] || "/dashboard"} replace />;
}

export default function App() {
  return (
    <ErrorBoundary>
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors closeButton />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/receipt/:orderId" element={<Protected><Receipt /></Protected>} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/dashboard" element={<Protected allow={["owner","manager","accountant"]}><Dashboard /></Protected>} />
              <Route path="/tables" element={<Protected allow={["owner","manager","waiter","cashier"]}><Tables /></Protected>} />
              <Route path="/reservations" element={<Protected allow={["owner","manager","cashier","waiter"]}><Reservations /></Protected>} />
              <Route path="/inventory" element={<Protected allow={["owner","manager","accountant"]}><Inventory /></Protected>} />
              <Route path="/new-order" element={<Protected allow={["owner","manager","waiter"]}><NewOrder /></Protected>} />
              <Route path="/new-order/:orderId" element={<Protected allow={["owner","manager","waiter"]}><NewOrder /></Protected>} />
              <Route path="/kitchen" element={<Protected allow={["owner","manager","kitchen","waiter"]}><Kitchen /></Protected>} />
              <Route path="/cashier" element={<Protected allow={["owner","manager","cashier"]}><Cashier /></Protected>} />
              <Route path="/menu" element={<Protected allow={["owner","manager"]}><MenuPage /></Protected>} />
              <Route path="/expenses" element={<Protected allow={["owner","manager","accountant"]}><Expenses /></Protected>} />
              <Route path="/debtors" element={<Protected allow={["owner","manager","cashier","accountant"]}><Debtors /></Protected>} />
              <Route path="/reports/daily" element={<Protected allow={["owner","manager","accountant","cashier"]}><ReportDaily /></Protected>} />
              <Route path="/reports/best-selling" element={<Protected allow={["owner","manager","accountant"]}><ReportBest /></Protected>} />
              <Route path="/reports/monthly" element={<Protected allow={["owner","manager","accountant"]}><ReportMonthly /></Protected>} />
              <Route path="/users" element={<Protected allow={["owner","manager"]}><Users /></Protected>} />
              <Route path="/settings" element={<Protected allow={["owner","manager"]}><Settings /></Protected>} />
            </Route>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="*" element={<HomeRedirect />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
    </ErrorBoundary>
  );
}
